import qs from 'qs';
import { nextTick } from 'vue';
import { createRouter, createWebHistory } from 'vue-router';
import type { LocationQuery } from 'vue-router';

import Maintenance from '@ui/components/Maintenance.vue';

import { authClient, stuut } from '@/api';
import { useQueryHistory } from '@/composables/useQueryHistory';
import { useQueryStore } from '@/composables/useQueryStore';
import { useProfile, useRouterHistory } from '@/store';
import { getSafeRedirectUrl } from '@/utils/redirectUrl';

import { playbookv1Route } from './playbook';
import {
  FeatureAccess,
  requireAdminRedirect,
  requireMailboxSettingsFlag,
  requiresFeatureAccess,
} from './utils';

export { FeatureAccess, requireAdminRedirect, requiresFeatureAccess };

let namedRouteRedirects: Map<string, string | undefined> | null = null;

const getNamedRedirectTarget = (
  redirect: ReturnType<(typeof router)['getRoutes']>[number]['redirect'],
): string | undefined => {
  if (!redirect || typeof redirect === 'function' || typeof redirect === 'string') {
    return undefined;
  }

  if ('name' in redirect && typeof redirect.name === 'string') {
    return redirect.name;
  }

  return undefined;
};

const getNamedRouteRedirects = () => {
  if (namedRouteRedirects) return namedRouteRedirects;

  namedRouteRedirects = new Map();

  for (const route of router.getRoutes()) {
    if (typeof route.name !== 'string') continue;

    namedRouteRedirects.set(route.name, getNamedRedirectTarget(route.redirect));
  }

  return namedRouteRedirects;
};

const traceRedirects = (startName: string, stopName?: string) => {
  const path: string[] = [];
  const visited = new Set<string>();
  let name: string | undefined = startName;

  while (name && !visited.has(name)) {
    path.push(name);

    if (name === stopName) {
      return { path, reachedStop: true };
    }

    visited.add(name);
    name = getNamedRouteRedirects().get(name);
  }

  return { path, reachedStop: false };
};

const getRedirectLoopChain = (
  redirectTarget: string,
  currentName: string,
  originName?: string,
): string[] | null => {
  const redirectTrace = traceRedirects(redirectTarget, currentName);

  if (!redirectTrace.reachedStop) {
    return null;
  }

  const originTrace =
    originName && originName !== currentName ? traceRedirects(originName, currentName) : null;
  const originPrefix = originTrace?.reachedStop ? originTrace.path.slice(0, -1) : [];

  return [...originPrefix, currentName, ...redirectTrace.path];
};

export const redirectWithLoopProtection = (
  to,
  next,
  redirectRouteName: string,
  guardName: string,
  query?: LocationQuery,
) => {
  const currentName = typeof to.name === 'string' ? to.name : undefined;

  if (!currentName) {
    return next({ name: redirectRouteName, query });
  }

  const loopChain = getRedirectLoopChain(
    redirectRouteName,
    currentName,
    typeof to.redirectedFrom?.name === 'string' ? to.redirectedFrom.name : undefined,
  );

  if (loopChain) {
    console.error(
      `[${guardName}] Redirect loop detected while redirecting "${currentName}" to "${redirectRouteName}". Falling back to "home". Chain: ${loopChain.join(' → ')}`,
    );
    return next({ name: 'home' });
  }

  return next({ name: redirectRouteName, query });
};

const peekFetchProfile = async (to, from, next) => {
  if (
    process.env.STUUT_MAINTENANCE_MODE?.toLowerCase() === 'true' ||
    process.env.STUUT_MAINTENANCE_MODE === '1'
  ) {
    next();
    return;
  }

  const profileStore = useProfile();

  if (authClient.authToken && !profileStore.profile?.id) {
    try {
      await profileStore.fetchProfile();
    } catch (err: any) {
      console.error(err.clientMessage);
    }
  }

  next();
};

const router = createRouter({
  history: createWebHistory(),

  parseQuery(query) {
    return qs.parse(query) as LocationQuery;
  },

  routes: [
    {
      component: () => import('@/pages/sign-in/index.vue'),
      name: 'sign-in',
      path: '/sign-in',
      props: (route) => ({ ...route.query }),
    },
    {
      component: () => import('@/pages/sign-up/index.vue'),
      name: 'sign-up',
      path: '/sign-up',
      props: (route) => ({ ...route.query }),
    },
    {
      component: () => import('@/pages/oauth/nylas.vue'),
      path: '/oauth/nylas',
    },
    {
      component: () => import('@/pages/oauth/mailbox.vue'),
      path: '/oauth/mailbox',
    },
    {
      component: Maintenance,
      name: 'maintenance',
      path: '/maintenance',
    },
    {
      component: () => import('@/pages/oauth/integrations.vue'),
      path: '/oauth/integrations',
    },
    {
      component: () => import('@/pages/sign-in/callback.vue'),
      name: 'auth-callback',
      path: '/auth/callback',
    },
    {
      component: () => import('@/pages/organization-registration/index.vue'),
      meta: { requiresAuth: true },
      name: 'organization-registration',
      path: '/register',
    },
    {
      beforeEnter: peekFetchProfile,
      children: [
        {
          component: () => import('@/pages/home/index.vue'),
          meta: { icon: 'fa-house', label: 'Home' },
          name: 'home',
          path: '',
        },
        {
          component: () => import('@/pages/onboarding/index.vue'),
          meta: { icon: 'fa-route', label: 'Setup', layout: 'dashboard' },
          name: 'onboarding.setup',
          path: 'onboarding',
        },
        {
          component: () => import('@/pages/onboarding/:flowKey/:stepKey.vue'),
          meta: { label: 'Setup', layout: 'dashboard' },
          name: 'onboarding.step',
          path: 'onboarding/:flowKey/:stepKey',
        },
        {
          children: [
            {
              component: () => import('@/pages/analyticsv2/index.vue'),
              meta: {
                label: 'Overview',
              },
              name: 'analytics.overview',
              path: 'overview',
            },
            {
              component: () => import('@/pages/analyticsv2/index.vue'),
              meta: {
                label: 'Performance',
              },
              name: 'analytics.performance',
              path: 'performance',
            },
            {
              component: () => import('@/pages/analyticsv2/index.vue'),
              meta: {
                label: 'Automation',
              },
              name: 'analytics.automation',
              path: 'automation',
            },
          ],
          component: () => import('@/pages/analyticsv2/index.vue'),
          meta: {
            icon: 'fa-chart-pie-simple',
            label: 'Analytics',
            layout: 'dashboard',
            requiresAuth: true,
          },
          name: 'analytics',
          path: 'analytics',
          redirect: { name: 'analytics.overview' },
        },
        {
          children: [
            {
              component: () => import('@/pages/workflows/index.vue'),
              meta: {
                label: 'Workflows',
              },
              name: 'workflows.index',
              path: 'index',
            },
            {
              component: () => import('@/pages/workflows/config.vue'),
              meta: {
                label: 'Config',
              },
              name: 'workflows.config',
              path: 'config',
            },
          ],
          component: () => import('@/pages/workflows.vue'),
          meta: {
            icon: 'fa-arrow-progress',
            label: 'Workflows',
          },
          redirect: {
            name: 'workflows.index',
          },
          name: 'workflows',
          path: 'workflows',
        },
        playbookv1Route,
        {
          children: [
            {
              children: [
                {
                  beforeEnter: requireAdminRedirect('home'),
                  component: () => import('@/pages/agents/collection/EmailSMS.vue'),
                  meta: {
                    icon: 'fa-comments',
                    label: 'Email and SMS',
                  },
                  name: 'agents.collection.emails-sms',
                  path: 'emails-sms',
                },
                {
                  beforeEnter: requireAdminRedirect('home'),
                  component: () => import('@/pages/agents/collection/p2p-dates.vue'),
                  meta: { icon: 'fa-calendar-check', label: 'Promise to pay' },
                  name: 'agents.collection.p2p-dates',
                  path: 'p2p-dates',
                },
                {
                  beforeEnter: requireAdminRedirect('home'),
                  component: () => import('@/pages/agents/collection/DunningRatesOverride.vue'),
                  meta: { icon: 'fa-arrows-rotate', label: 'Dunning rates override' },
                  name: 'agents.collection.dunning-rates-override',
                  path: 'dunning-rates-override',
                },
                {
                  beforeEnter: requireAdminRedirect('home'),
                  component: () => import('@/pages/agents/collection/PrimaryContact.vue'),
                  meta: { icon: 'fa-address-book', label: 'Primary contact' },
                  name: 'agents.collection.primary-contact',
                  path: 'primary-contact',
                },
                {
                  beforeEnter: requireAdminRedirect('home'),
                  component: () => import('@/pages/agents/collection/call-agent.vue'),
                  meta: { icon: 'fa-headset', label: 'Call agent' },
                  name: 'agents.collection.call-agent',
                  path: 'call-agent',
                },
                {
                  beforeEnter: requireAdminRedirect('home'),
                  component: () => import('@/pages/agents/collection/centralized-inbox.vue'),
                  meta: { label: 'Centralized Inbox', icon: 'fa-mailbox fas' },
                  name: 'agents.collection.centralized-inbox',
                  path: 'centralized-inbox',
                },
              ],
              component: () => import('@/pages/agents/collection.vue'),
              meta: {
                description: 'Define how your agent monitors and follows up on open invoices.',
                icon: 'fa-album-collection-circle-plus',
                label: 'Collections',
              },
              name: 'agents.collection',
              path: 'collection',
              redirect: {
                name: 'agents.collection.emails-sms',
              },
            },
            {
              beforeEnter: requiresFeatureAccess(FeatureAccess.CashApp, 'agents'),
              children: [
                {
                  component: () => import('@/pages/agents/cash-app/communications.vue'),
                  meta: { icon: 'fa-envelope', label: 'Communications' },
                  name: 'agents.cash-app.communications',
                  path: 'communications',
                },
                {
                  component: () => import('@/pages/agents/cash-app/auto-post.vue'),
                  meta: { icon: 'fa-bolt', label: 'Auto-Post' },
                  name: 'agents.cash-app.auto-post',
                  path: 'auto-post',
                },
              ],
              component: () => import('@/pages/agents/cash-app.vue'),
              meta: {
                description: 'Configure how your agent applies and reconciles payments.',
                icon: 'fa-money-bill-transfer',
                label: 'Cash App',
              },
              name: 'agents.cash-app',
              path: 'cash-app',
              redirect: {
                name: 'agents.cash-app.auto-post',
              },
            },
            {
              children: [
                {
                  component: () => import('@/pages/agents/deductions/config.vue'),
                  meta: { icon: 'fa-book-open', label: 'Agent Knowledge' },
                  name: 'agents.deductions.agent-knowledge',
                  path: 'agent-knowledge',
                },
              ],
              component: () => import('@/pages/agents/deductions.vue'),
              meta: { icon: 'fa-percentage', label: 'Deductions' },
              name: 'agents.deductions',
              path: 'deductions',
              redirect: { name: 'agents.deductions.agent-knowledge' },
            },
          ],
          component: () => import('@/pages/agents/layout.vue'),
          meta: {
            description: 'Manage agent rules and triggers',
            icon: 'fa-gear',
            label: 'Agent Settings',
          },
          name: 'agents',
          path: 'agents',
          redirect: {
            name: 'agents.collection',
          },
        },
        {
          component: () => import('@/pages/approvals/index.vue'),
          meta: { breadcrumb: 'Approvals' },
          name: 'approvals',
          path: 'approvals',
        },
        {
          children: [
            {
              component: () => import('@/pages/customers/index.vue'),
              meta: {
                icon: 'fa-users',
                label: 'Customers',
                preserveQuery: false,
              },
              name: 'customers',
              path: '',
            },
            {
              children: [
                {
                  component: () => import('@/pages/customers/:id/index.vue'),
                  meta: { label: 'Customer Overview' },
                  name: 'customers.customer',
                  path: '',
                },
                {
                  component: () => import('@/pages/customers/:id/invoices.vue'),
                  meta: { label: 'Customer Invoices' },
                  name: 'customers.customer.invoices',
                  path: 'invoices',
                },
                {
                  component: () => import('@/pages/customers/:id/credit-memos.vue'),
                  meta: { label: 'Credit Memos' },
                  name: 'customers.customer.credit-memos',
                  path: 'credit-memos',
                },
                {
                  component: () => import('@/pages/customers/:id/transactions.vue'),
                  meta: { label: 'Customer Transactions' },
                  name: 'customers.customer.transactions',
                  path: 'transactions',
                },
                {
                  component: () => import('@/pages/customers/:id/tasks.vue'),
                  meta: { label: 'Customer Tasks' },
                  name: 'customers.customer.tasks',
                  path: 'tasks',
                },
                {
                  component: () => import('@/pages/customers/:id/activity.vue'),
                  meta: { label: 'Customer Activity' },
                  name: 'customers.customer.activity',
                  path: 'activity',
                },
                {
                  component: () => import('@/pages/customers/:id/payments.vue'),
                  meta: { label: 'Customer Payments' },
                  name: 'customers.customer.payments',
                  path: 'payments',
                },
                {
                  beforeEnter: requiresFeatureAccess(FeatureAccess.BaseDisputes, 'customers'),
                  component: () => import('@/pages/customers/:id/disputes.vue'),
                  meta: { label: 'Customer disputes' },
                  name: 'customers.customer.disputes',
                  path: 'disputes',
                },
                {
                  component: () => import('@/pages/customers/:id/outreach-stage-evals.vue'),
                  meta: { label: 'Outreach Evaluations' },
                  name: 'customers.customer.outreach-evaluations',
                  path: 'outreach-evaluations',
                },
                {
                  component: () => import('@/pages/customers/:id/group.vue'),
                  meta: { label: 'Group' },
                  name: 'customers.customer.group',
                  path: 'group',
                },
                {
                  component: () => import('@/pages/customers/:id/risk.vue'),
                  meta: { label: 'Risk' },
                  name: 'customers.customer.risk',
                  path: 'risk',
                },
                {
                  component: () => import('@/pages/customers/:id/ap-portal.vue'),
                  meta: {
                    label: 'AP Portal',
                  },
                  name: 'customers.customer.ap-portal',
                  path: 'ap-portal',
                },
              ],
              component: () => import('@/pages/customers/:id/layout.vue'),
              meta: {
                breadcrumb: async (route) => {
                  const partner = await stuut.partners.retrieve(route.params.id);
                  return `${partner.name || partner.legal_name || partner.email || 'Customer'}`;
                },
              },
              path: ':id',
              props: (route) => ({ key: route.params.id as string }),
            },
          ],
          meta: { breadcrumb: 'Back to Customers' },
          path: 'customers',
        },
        {
          beforeEnter: requiresFeatureAccess(FeatureAccess.Deductions),
          children: [
            {
              component: () => import('@/pages/deductions/deductions.vue'),
              meta: {
                icon: 'fa-percentage',
                label: 'Deductions',
                preserveQuery: false,
                route: 'deductions.list',
                storeQuery: true,
              },
              name: 'deductions.list',
              path: 'deductions',
            },
            {
              component: () => import('@/pages/deductions/deductions/deduction.vue'),
              meta: { label: 'Deductions' },
              name: 'deductions.deduction',
              path: 'deductions/:id',
            },
            {
              component: () => import('@/pages/deductions/claims.vue'),
              meta: {
                label: 'Claims',
                preserveQuery: false,
                storeQuery: true,
              },
              name: 'deductions.claims',
              path: 'claims',
            },
            {
              component: () => import('@/pages/deductions/claims/claim.vue'),
              meta: { label: 'Claims' },
              name: 'deductions.claims.claim',
              path: 'claims/:id',
            },
            {
              component: () => import('@/pages/deductions/uploads.vue'),
              meta: {
                label: 'Uploads',
                preserveQuery: false,
                route: 'deductions.uploads',
              },
              name: 'deductions.uploads',
              path: 'uploads',
            },
            {
              component: () => import('@/pages/deductions/ap-portal.vue'),
              meta: {
                label: 'AP Portal',
                preserveQuery: false,
                route: 'deductions.ap-portal',
              },
              name: 'deductions.ap-portal',
              path: 'ap-portal',
            },
            {
              component: () => import('@/pages/deductions/knowledge.vue'),
              meta: {
                label: 'Knowledge',
                preserveQuery: false,
                route: 'deductions.knowledge',
              },
              name: 'deductions.knowledge',
              path: 'knowledge',
            },
            {
              component: () => import('@/pages/deductions/settings.vue'),
              meta: {
                label: 'All Settings',
                preserveQuery: false,
              },
              name: 'deductions.settings',
              path: 'settings',
            },
          ],
          component: () => import('@/pages/deductions/layout.vue'),
          meta: {
            icon: 'fa-divide',
            label: 'Deductions',
          },
          name: 'deductions',
          path: 'deductions',
          redirect: { name: 'deductions.list' },
        },
        {
          beforeEnter: requiresFeatureAccess(FeatureAccess.BaseDisputes),
          children: [
            {
              children: [
                {
                  component: () => import('@/pages/disputes/:id/notes.vue'),
                  meta: {
                    icon: '',
                    label: 'Disputes',
                    preserveQuery: false,
                    route: 'disputes',
                  },
                  name: 'disputes.dispute.notes',
                  path: 'notes',
                },
                {
                  component: () => import('@/pages/disputes/:id/communication.vue'),
                  meta: {
                    icon: '',
                    label: 'Disputes',
                    preserveQuery: false,
                    route: 'disputes',
                  },
                  name: 'disputes.dispute.communication',
                  path: 'communication',
                },
                {
                  component: () => import('@/pages/disputes/:id/tasks.vue'),
                  meta: {
                    icon: '',
                    label: 'Disputes',
                    preserveQuery: false,
                    route: 'disputes',
                  },
                  name: 'disputes.dispute.tasks',
                  path: 'tasks',
                },
                {
                  component: () => import('@/pages/disputes/:id/activity.vue'),
                  meta: {
                    icon: '',
                    label: 'Disputes',
                    preserveQuery: false,
                    route: 'disputes',
                  },
                  name: 'disputes.dispute.activity',
                  path: 'activity',
                },
                {
                  component: () => import('@/pages/disputes/:id/documents.vue'),
                  meta: {
                    icon: '',
                    label: 'Disputes',
                    preserveQuery: false,
                    route: 'disputes',
                  },
                  name: 'disputes.dispute.documents',
                  path: 'documents',
                },
                {
                  component: () => import('@/pages/disputes/:id/overview.vue'),
                  meta: {
                    icon: '',
                    label: 'Disputes',
                    preserveQuery: false,
                    route: 'disputes',
                  },
                  name: 'disputes.dispute.overview',
                  path: 'overview',
                },
              ],
              component: () => import('@/pages/disputes/:id/layout.vue'),
              meta: {
                label: 'Disputes',
                preserveQuery: false,
                route: 'disputes',
              },
              name: 'disputes.dispute',
              path: ':id',
              props: (route) => ({ key: route.params.id as string }),
              redirect: { name: 'disputes.dispute.overview' },
            },
            {
              children: [
                {
                  component: () => import('@/pages/disputes/overview.vue'),
                  meta: {
                    icon: 'fa-chart-simple',
                    label: 'Overview',
                    preserveQuery: false,
                    route: 'disputes',
                  },
                  name: 'disputes.overview',
                  path: 'overview',
                },
                {
                  component: () => import('@/pages/disputes/list.vue'),
                  meta: {
                    icon: 'fa-list',
                    label: 'List',
                    preserveQuery: false,
                    route: 'disputes',
                    storeQuery: true,
                  },
                  name: 'disputes.list',
                  path: 'list',
                },
              ],
              component: () => import('@/pages/disputes/layout.vue'),
              meta: {
                icon: 'fa-balance-scale',
                label: 'Disputes',
                preserveQuery: false,
                route: 'disputes',
              },
              name: 'disputes.layout',
              path: '',
              redirect: { name: 'disputes.overview' },
            },
          ],
          meta: { statusCountType: 'disputes' },
          path: 'disputes',
        },
        {
          children: [
            {
              component: () => import('@/pages/credit-memos/index.vue'),
              meta: {
                icon: 'fa-file-minus',
                label: 'Credit Memos',
                preserveQuery: false,
              },
              name: 'credit-memos',
              path: '',
            },
            {
              children: [
                {
                  component: () => import('@/pages/credit-memos/:id/index.vue'),
                  meta: { label: 'Credit Memo' },
                  name: 'credit-memos.credit-memo',
                  path: '',
                },
              ],
              component: () => import('@/pages/credit-memos/:id/layout.vue'),
              path: ':id',
            },
          ],
          meta: { breadcrumb: 'Back to Credit Memos' },
          path: 'credit-memos',
        },
        {
          component: () => import('@/pages/payments/index.vue'),
          meta: {
            icon: 'fa-sack-dollar',
            label: 'Payments',
            preserveQuery: false,
            storeQuery: true,
          },
          name: 'payments',
          path: 'payments',
        },
        {
          children: [
            {
              component: () => import('@/pages/invoices/index.vue'),
              meta: {
                icon: 'fa-file-invoice-dollar',
                label: 'Invoices',
                preserveQuery: false,
              },
              name: 'invoices',
              path: '',
            },
            {
              children: [
                {
                  component: () => import('@/pages/invoices/:id/index.vue'),
                  meta: { label: 'Invoice' },
                  name: 'invoices.invoice',
                  path: '',
                },
              ],
              component: () => import('@/pages/invoices/:id/layout.vue'),
              meta: {
                breadcrumb: async (route) => {
                  const invoice = await stuut.invoices.retrieve(route.params.id);
                  return `${invoice.number || invoice.remote_id || 'Invoice'}`;
                },
              },
              path: ':id',
            },
          ],
          meta: { breadcrumb: 'Back to Invoices' },
          path: 'invoices',
        },
        {
          children: [
            {
              children: [
                {
                  component: () => import('@/pages/collection/emails.vue'),
                  name: 'collection.emails.email',
                  path: ':id',
                },
              ],
              component: () => import('@/pages/collection/emails.vue'),
              meta: { label: 'Email Inbox' },
              name: 'collection.emails',
              path: 'emails',
            },
            {
              children: [
                {
                  component: () => import('@/pages/collection/calls.vue'),
                  name: 'collection.calls.call',
                  path: ':id',
                },
              ],
              component: () => import('@/pages/collection/calls.vue'),
              meta: { label: 'Call Inbox' },
              name: 'collection.calls',
              path: 'calls',
            },
            {
              children: [
                {
                  component: () => import('@/pages/collection/sms/thread.vue'),
                  name: 'collection.sms.thread',
                  path: ':id',
                },
              ],
              component: () => import('@/pages/collection/sms.vue'),
              meta: { label: 'SMS Inbox' },
              name: 'collection.sms',
              path: 'sms',
            },
          ],
          component: () => import('@/pages/collection/layout.vue'),
          meta: { icon: 'fa-paper-plane', label: 'Collections', statusCountType: 'inbox' },
          name: 'collections',
          path: 'collection',
          redirect: { name: 'collection.emails' },
        },
        {
          beforeEnter: requiresFeatureAccess(FeatureAccess.CashApp),
          children: [
            {
              component: () => import('@/pages/cash-app/index.vue'),
              meta: { icon: 'fa-home', label: 'Overview' },
              name: 'cash-app.overview',
              path: '',
            },
            {
              children: [
                {
                  component: () => import('@/pages/cash-app/remittances/:id.vue'),
                  meta: { label: 'Remittance' },
                  name: 'cash-app.remittances.remittance',
                  path: ':id',
                },
              ],
              component: () => import('@/pages/cash-app/remittances/index.vue'),
              meta: { icon: 'fa-file-check', label: 'Remittances', storeQuery: true },
              name: 'cash-app.remittances',
              path: 'remittances',
            },
            {
              children: [
                {
                  component: () => import('@/pages/cash-app/bank-statements/:id.vue'),
                  meta: { label: 'Bank Statement' },
                  name: 'cash-app.bank-statements.statement',
                  path: ':id',
                },
              ],
              component: () => import('@/pages/cash-app/bank-statements/index.vue'),
              meta: { icon: 'fa-file-invoice', label: 'Bank Statements', storeQuery: true },
              name: 'cash-app.bank-statements',
              path: 'bank-statements',
            },
            {
              component: () => import('@/pages/cash-app/transactions/index.vue'),
              meta: { icon: 'fa-sack', label: 'Cash', storeQuery: true },
              name: 'cash-app.transactions',
              path: 'transactions',
            },
          ],
          component: () => import('@/pages/cash-app/layout.vue'),
          meta: { icon: 'fa-money-bill-transfer', label: 'Cash App' },
          name: 'cash-app',
          path: 'cash-app',
          redirect: { name: 'cash-app.overview' },
        },
        {
          component: () => import('@/pages/cash-app/transactions/:id.vue'),
          meta: { label: 'Bank Transaction' },
          name: 'cash-app.transactions.transaction',
          path: 'cash-app/transactions/:id',
        },
        {
          component: () => import('@/pages/cash-app/bank-statements/:id.vue'),
          meta: { label: 'Bank Statement' },
          name: 'cash-app.bank-statements.statement',
          path: 'cash-app/bank-statements/:id',
        },
        {
          component: () => import('@/pages/cash-app/remittances/:id.vue'),
          meta: { label: 'Remittance' },
          name: 'cash-app.remittances.remittance',
          path: 'cash-app/remittances/:id',
        },
        {
          component: () => import('@/pages/payments/:payment.vue'),
          meta: { fullscreen: true, label: 'Payment' },
          name: 'payments.payment',
          path: 'payments/:payment',
        },
        {
          children: [
            {
              children: [
                {
                  children: [
                    {
                      component: () =>
                        import('@/pages/settings/connectors/connections/:id/index.vue'),
                      name: 'integrations.connection',
                      path: '',
                    },
                    {
                      component: () =>
                        import('@/pages/settings/connectors/connections/:id/logs.vue'),
                      name: 'integrations.connection.logs',
                      path: 'logs',
                    },
                    {
                      component: () =>
                        import('@/pages/settings/connectors/connections/:id/webhooks.vue'),
                      name: 'integrations.connection.webhooks',
                      path: 'webhooks',
                    },
                    {
                      component: () =>
                        import('@/pages/settings/connectors/connections/:id/settings.vue'),
                      name: 'integrations.connection.settings',
                      path: 'settings',
                    },
                  ],
                  component: () => import('@/pages/settings/connectors/connections/:id/layout.vue'),
                  meta: {
                    label: 'Integration',
                  },
                  path: 'connections/:id',
                  redirect: { name: 'integrations.connection' },
                },
                {
                  beforeEnter: requireAdminRedirect('settings.connectors.connected-email'),
                  children: [
                    {
                      component: () =>
                        import('@/pages/settings/connectors/connected-apps/index.vue'),
                      meta: {
                        label: 'Connected Apps',
                        tab: 'settings.connectors.connected-apps',
                      },
                      name: 'settings.connectors.connected-apps.index',
                      path: '',
                    },
                    {
                      children: [
                        {
                          component: () =>
                            import('@/pages/settings/connectors/connected-apps/:id/index.vue'),
                          name: 'integrations.link',
                          path: '',
                        },
                        {
                          component: () =>
                            import('@/pages/settings/connectors/connected-apps/:id/logs.vue'),
                          name: 'integrations.link.logs',
                          path: 'logs',
                        },
                        {
                          component: () =>
                            import('@/pages/settings/connectors/connected-apps/:id/webhooks.vue'),
                          name: 'integrations.link.webhooks',
                          path: 'webhooks',
                        },
                        {
                          component: () =>
                            import('@/pages/settings/connectors/connected-apps/:id/field-mappings.vue'),
                          name: 'integrations.link.field-mapping',
                          path: 'field-mappings',
                        },
                        {
                          component: () =>
                            import('@/pages/settings/connectors/connected-apps/:id/settings.vue'),
                          name: 'integrations.link.settings',
                          path: 'settings',
                        },
                        {
                          component: () =>
                            import('@/pages/settings/connectors/connected-apps/:id/reports.vue'),
                          name: 'integrations.link.reports',
                          path: 'reports',
                        },
                      ],
                      component: () =>
                        import('@/pages/settings/connectors/connected-apps/:id/layout.vue'),
                      meta: {
                        label: 'Integration',
                      },
                      path: ':id',
                      redirect: { name: 'integrations.link' },
                    },
                  ],
                  component: () => import('@/pages/settings/connectors/connected-apps.vue'),
                  meta: {
                    label: 'Connected Apps',
                    tab: 'settings.connectors.connected-apps',
                  },
                  name: 'settings.connectors.connected-apps',
                  path: 'connected-apps',
                  redirect: {
                    name: 'settings.connectors.connected-apps.index',
                  },
                },
                {
                  // Flag on → the new mailbox page supersedes this; redirect there.
                  beforeEnter: requireMailboxSettingsFlag(false, 'settings.mail'),
                  component: () => import('@/pages/settings/connectors/connected-email.vue'),
                  meta: { label: 'Connected Email' },
                  name: 'settings.connectors.connected-email',
                  path: 'connected-email',
                },
                {
                  // Flag off → fall back to the legacy Connected Email page.
                  beforeEnter: requireMailboxSettingsFlag(
                    true,
                    'settings.connectors.connected-email',
                  ),
                  component: () => import('@/pages/settings/connectors/connected-email-v2.vue'),
                  meta: { label: 'Connected Email', tab: 'settings.connectors.connected-email' },
                  name: 'settings.connectors.connected-email-v2',
                  path: 'connected-email-v2',
                },
                {
                  beforeEnter: requireAdminRedirect('settings.connectors.connected-email'),
                  component: () => import('@/pages/settings/connectors/ap-portals.vue'),
                  meta: { label: 'AP Portals' },
                  name: 'settings.connectors.ap-portals',
                  path: 'ap-portals',
                },
                {
                  beforeEnter: requireAdminRedirect('settings.connectors.connected-email'),
                  component: () => import('@/pages/settings/connectors/all-connectors.vue'),
                  meta: { label: 'All Connectors' },
                  name: 'settings.connectors.all-connectors',
                  path: 'all-connectors',
                },
                {
                  beforeEnter: requireAdminRedirect('settings.connectors.connected-email'),
                  component: () => import('@/pages/settings/connectors/connected-bank.vue'),
                  meta: { icon: 'fa-bank', label: 'Bank Accounts' },
                  name: 'settings.connectors.bank-accounts',
                  path: 'bank-accounts',
                },
              ],
              component: () => import('@/pages/settings/connectors.vue'),
              meta: {
                description: 'ERP, email, bank, AP portal, and more',
                icon: 'fa-plug',
                label: 'Connectors',
              },
              name: 'settings.connectors',
              path: 'connectors',
              redirect: {
                name: 'settings.connectors.connected-apps',
              },
            },
            {
              // Flag off → fall back to the legacy Connected Email page.
              beforeEnter: requireMailboxSettingsFlag(true, 'settings.connectors.connected-email'),
              component: () => import('@/pages/settings/connectors/connected-email-v2.vue'),
              meta: {
                description: 'Connect and manage sending mailboxes',
                icon: 'fa-envelope',
                label: 'Mail',
              },
              name: 'settings.mail',
              path: 'mail',
            },
            {
              component: () => import('@/pages/settings/profile.vue'),
              meta: {
                description: 'Your personal settings',
                icon: 'fa-user',
                label: 'Settings',
              },
              name: 'settings.profile',
              path: 'profile',
            },
            {
              children: [
                {
                  children: [
                    {
                      component: () => import('@/pages/settings/exports/export.vue'),
                      meta: {
                        description: 'View and download data exports',
                        icon: 'fa-file-export',
                        label: 'Exports',
                      },
                      name: 'settings.exports.export',
                      path: ':id',
                    },
                  ],
                  component: () => import('@/pages/settings/exports/history.vue'),
                  meta: {
                    description: 'View and download data exports',
                    icon: 'fa-file-export',
                    label: 'Export History',
                  },
                  name: 'settings.exports.history',
                  path: 'history',
                },
                {
                  component: () => import('@/pages/settings/exports/scheduled-reports.vue'),
                  meta: {
                    description: 'Configure scheduled email reports',
                    icon: 'fa-calendar',
                    label: 'Scheduled Reports',
                  },
                  name: 'settings.exports.scheduled-reports',
                  path: 'scheduled-reports',
                },
              ],
              component: () => import('@/pages/settings/exports/index.vue'),
              meta: {
                description: 'View and download data exports',
                icon: 'fa-file-export',
                label: 'Exports',
              },
              name: 'settings.exports',
              path: 'exports',
              redirect: { name: 'settings.exports.history' },
            },
            {
              beforeEnter: requireAdminRedirect('settings.connectors.connected-email'),
              children: [
                {
                  beforeEnter: requireAdminRedirect('settings.connectors.connected-email'),
                  component: () => import('@/pages/settings/organization/preferences.vue'),
                  meta: { label: 'Settings' },
                  name: 'settings.organization.preferences',
                  path: 'preferences',
                },
                {
                  component: () => import('@/pages/settings/organization/team.vue'),
                  meta: { label: 'Team' },
                  name: 'settings.organization.team',
                  path: 'team',
                },
                {
                  component: () => import('@/pages/settings/organization/alerts.vue'),
                  name: 'settings.organization.alerts',
                  path: 'alerts',
                },
                {
                  component: () => import('@/pages/settings/organization/tags.vue'),
                  name: 'settings.organization.tags',
                  path: 'tags',
                },
                {
                  component: () => import('@/pages/settings/organization/payments.vue'),
                  name: 'settings.organization.payments',
                  path: 'payments',
                },
                {
                  component: () => import('@/pages/settings/organization/kyb.vue'),
                  name: 'settings.organization.kyb',
                  path: 'kyb',
                },
                {
                  component: () => import('@/pages/settings/organization/admin.vue'),
                  meta: { label: 'Admin' },
                  name: 'settings.organization.admin',
                  path: 'admin',
                },
              ],
              component: () => import('@/pages/settings/organization.vue'),
              meta: {
                description: 'Workspace and team settings',
                icon: 'fa-earth-americas',
                label: 'Organization Settings',
              },
              name: 'settings.organization',
              path: 'organization',
              redirect: {
                name: 'settings.organization.preferences',
              },
            },
            // {
            //   name: 'settings.ap-portal',
            //   path: 'ap-portal',
            //   component: () => import('@/pages/settings/ap-portal.vue'),
            //   beforeEnter: requireAdminRedirect(
            //     'settings.connectors.connected-email',
            //   ),
            // },
            // {
            //   name: 'settings.billing',
            //   path: 'billing',
            //   component: () => import('@/pages/settings/billing.vue'),
            // },
          ],
          component: () => import('@/pages/settings/index.vue'),
          meta: {
            description: 'Organization and personal settings',
            icon: 'fa-gears',
            label: 'General Settings',
          },
          name: 'settings',
          path: '/settings',
          redirect: { name: 'settings.connectors' },
        },
        {
          children: [
            {
              component: () => import('@/pages/tasks/all-tasks.vue'),
              meta: {
                label: 'Tasks',
                route: 'tasks',
              },
              name: 'tasks.all-tasks',
              path: 'all-tasks',
            },
            {
              component: () => import('@/pages/tasks/by-customer.vue'),
              meta: {
                label: 'Tasks',
                route: 'tasks',
              },
              name: 'tasks.by-customer',
              path: 'by-customer',
            },
            {
              component: () => import('@/pages/tasks/by-type.vue'),
              meta: {
                label: 'Tasks',
                route: 'tasks',
              },
              name: 'tasks.by-type',
              path: 'by-type',
            },
            {
              component: () => import('@/pages/tasks/agent-log.vue'),
              meta: {
                label: 'Tasks',
                route: 'tasks',
              },
              name: 'tasks.agent-log',
              path: 'agent-log',
            },
          ],
          component: () => import('@/pages/tasks/layout.vue'),
          meta: {
            icon: 'fa-list-check',
            label: 'Tasks',
            preserveQuery: false,
            route: 'tasks',
            statusCountType: 'tasks',
          },
          name: 'tasks',
          path: 'tasks',
          redirect: {
            name: 'tasks.all-tasks',
            query: { group_view_mode: 'individual' },
          },
        },
        {
          component: () => import('@/pages/audit-log/index.vue'),
          meta: { label: 'Audit Log' },
          name: 'audit-log',
          path: 'audit-log',
        },
      ],
      component: () => import('@/layout/index.vue'),
      meta: { requiresAuth: true },
      path: '/',
    },
    {
      path: '/:pathMatch(.*)*',
      redirect: '/',
    },
  ],

  scrollBehavior() {
    return { top: 0 };
  },

  stringifyQuery(query) {
    const result = qs.stringify(query);

    return result || '';
  },
});

router.beforeEach((to, from, next) => {
  if (
    process.env.STUUT_MAINTENANCE_MODE?.toLowerCase() === 'true' ||
    process.env.STUUT_MAINTENANCE_MODE === '1'
  ) {
    if (to.name === 'maintenance') {
      return next();
    }
    return next({ name: 'maintenance' });
  }

  const routerHistory = useRouterHistory();
  routerHistory.setPreviousRoute(from.fullPath);
  const authTokenExists = authClient.hasAuthToken;

  if ((to.name === 'sign-in' || to.name === 'sign-up') && authTokenExists) {
    document.querySelector('body')?.classList?.remove('sign-in');
    const safeRedirect = getSafeRedirectUrl(to.query.redirect as string);
    return next(safeRedirect || { name: 'home' });
  }

  if (to.matched.some((record) => record.meta.requiresAuth) && !authTokenExists) {
    if (authClient.hasRefreshToken) {
      return authClient
        .loginWithRefreshToken()
        .then((loginData) => {
          const profileStore = useProfile();
          profileStore.$patch({ profile: loginData.profile });
          document.querySelector('body')?.classList?.remove('sign-in');
          next();
        })
        .catch(() => next({ name: 'sign-in', query: { redirect: to.fullPath } }));
    }

    return next({ name: 'sign-in', query: { redirect: to.fullPath } });
  }

  const { getQuery, setQuery } = useQueryHistory();
  const { clearStoreQuery, getStoreQuery, setStoreQuery } = useQueryStore();

  // On route exit: save or update the current query
  if (from.meta?.preserveQuery && Object.keys(from.query).length > 0) {
    setQuery(from.name, from.query);
  }

  if (from.meta?.storeQuery && Object.keys(from.query).length > 0) {
    setStoreQuery(from.name, from.query);
  }

  // On route entry: if no query is provided, restore the saved query
  // Not performing a merge here to prevent issues like overriding an explicit
  // link with a conflicting saved state.
  if (to.meta?.preserveQuery && !Object.keys(to.query).length) {
    const savedQuery = getQuery(to.name);

    if (Object.keys(savedQuery).length) {
      return next({ ...to, query: savedQuery });
    }
  }

  if (to.meta?.storeQuery && getStoreQuery(to.name)) {
    clearStoreQuery(to.name);
  }

  return next();
});

router.afterEach((to) => {
  // Use next tick to handle router history correctly
  // see: https://github.com/vuejs/vue-router/issues/914#issuecomment-384477609
  nextTick(() => {
    let pageTitle = 'Stuut';
    if (to.meta.label) {
      pageTitle += ` - ${to.meta.label}`;
    }
    document.title = pageTitle;
  });
});

export default router;
